import Image from "next/image";
import { CircleCheck, FileText, Hash, Lock, Sparkles, Zap } from "lucide-react";
import type { CSSProperties } from "react";

import type { AiAgentsHowStepId, AiAgentsSubpageCopy } from "./ai-agents-page-copy";

type Ui = AiAgentsSubpageCopy["how"]["ui"];

function ChatView({ ui }: { ui: Ui }) {
  return (
    <div className="cc-agent-view cc-agent-view--chat">
      <p className="cc-agent-bubble cc-agent-bubble--user">{ui.chat.user}</p>
      <div className="cc-agent-bubble cc-agent-bubble--agent">
        <span className="cc-agent-avatar">
          <Sparkles size={13} strokeWidth={2.2} />
        </span>
        <div>
          <p>{ui.chat.agent}</p>
          <ul>
            {ui.chat.options.map((option, index) => (
              <li key={option} style={{ "--cc-ag-i": index } as CSSProperties}>
                <span>{option}</span>
                <em>{ui.chat.build}</em>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ConnectView({ ui }: { ui: Ui }) {
  const count = ui.connect.logos.length;
  return (
    <div className="cc-agent-view cc-agent-view--connect">
      <span className="cc-agent-view__title">{ui.connect.title}</span>
      <div className="cc-agent-orbit">
        <svg className="cc-agent-orbit__lines" viewBox="0 0 200 200">
          {ui.connect.logos.map((logo, index) => {
            const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
            return (
              <line
                key={logo}
                x1="100"
                y1="100"
                x2={(100 + Math.cos(angle) * 78).toFixed(1)}
                y2={(100 + Math.sin(angle) * 78).toFixed(1)}
                style={{ "--cc-ag-i": index } as CSSProperties}
              />
            );
          })}
        </svg>
        <span className="cc-agent-orbit__core">
          <Sparkles size={18} strokeWidth={2.2} />
          {ui.connect.agent}
        </span>
        {ui.connect.logos.map((logo, index) => {
          const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
          return (
            <span
              key={logo}
              className="cc-agent-orbit__logo"
              style={
                {
                  left: `${50 + Math.cos(angle) * 39}%`,
                  top: `${50 + Math.sin(angle) * 39}%`,
                  "--cc-ag-i": index,
                } as CSSProperties
              }
            >
              <Image src={logo} alt="" width={22} height={22} unoptimized />
            </span>
          );
        })}
      </div>
      <span className="cc-agent-view__more">{ui.connect.more}</span>
    </div>
  );
}

function RunView({ ui }: { ui: Ui }) {
  return (
    <div className="cc-agent-view cc-agent-view--run">
      <span className="cc-agent-view__trigger">
        <Zap size={12} strokeWidth={2.4} />
        {ui.run.trigger}
      </span>
      <strong className="cc-agent-view__title">{ui.run.title}</strong>
      <ol className="cc-agent-steps">
        {ui.run.steps.map((step, index) => (
          <li key={step} style={{ "--cc-ag-i": index } as CSSProperties}>
            <CircleCheck size={15} strokeWidth={2.2} />
            {step}
          </li>
        ))}
      </ol>
      <p className="cc-agent-view__result">{ui.run.result}</p>
    </div>
  );
}

function ApproveView({ ui }: { ui: Ui }) {
  return (
    <div className="cc-agent-view cc-agent-view--approve">
      <span className="cc-agent-view__badge">
        <i />
        {ui.approve.title}
      </span>
      <div className="cc-agent-bubble cc-agent-bubble--agent">
        <span className="cc-agent-avatar">
          <Sparkles size={13} strokeWidth={2.2} />
        </span>
        <div>
          <p>{ui.approve.question}</p>
          <span className="cc-agent-file">
            <FileText size={14} strokeWidth={2} />
            {ui.approve.file}
          </span>
          <span className="cc-agent-actions">
            <em>{ui.approve.approve}</em>
            <em data-variant="ghost">{ui.approve.edit}</em>
          </span>
        </div>
      </div>
      <span className="cc-agent-view__rule">
        <Lock size={12} strokeWidth={2.4} />
        {ui.approve.rule}
      </span>
    </div>
  );
}

function ReportView({ ui }: { ui: Ui }) {
  return (
    <div className="cc-agent-view cc-agent-view--report">
      <span className="cc-agent-channel">
        <Hash size={13} strokeWidth={2.4} />
        {ui.report.channel}
      </span>
      <div className="cc-agent-message">
        <span className="cc-agent-avatar">
          <Sparkles size={13} strokeWidth={2.2} />
        </span>
        <div>
          <span className="cc-agent-message__meta">
            <b>{ui.report.app}</b>
            {ui.report.time}
          </span>
          <p>{ui.report.message}</p>
          <ul>
            {ui.report.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>{ui.report.ask}</p>
        </div>
      </div>
      <p className="cc-agent-bubble cc-agent-bubble--user">{ui.report.reply}</p>
      <span className="cc-agent-triggers">
        {ui.report.triggers.map((trigger) => (
          <span key={trigger}>
            <Zap size={11} strokeWidth={2.4} />
            {trigger}
          </span>
        ))}
      </span>
    </div>
  );
}

/** One small product view per "how it works" step. Decorative. */
export function CcAgentHowView({ id, ui }: { id: AiAgentsHowStepId; ui: Ui }) {
  if (id === "discover") return <ChatView ui={ui} />;
  if (id === "connect") return <ConnectView ui={ui} />;
  if (id === "run") return <RunView ui={ui} />;
  if (id === "approve") return <ApproveView ui={ui} />;
  return <ReportView ui={ui} />;
}
